import { Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { formatMoney } from "@mavatech/shared";
import { Select } from "@/components/ui";

const axisEuro = (v: number) =>
  Math.abs(v) >= 1000
    ? `${(v / 1000).toLocaleString("de-AT", { maximumFractionDigits: 1 })} Tsd. €`
    : `${v.toLocaleString("de-AT")} €`;

export interface FinanceMonth {
  label: string;
  incomeNetCents: number;
  expenseNetCents: number;
  profitNetCents: number;
}

/**
 * Gemeinsames Diagramm (ersetzt bei aktivem Modul "Ausgaben & Gewinnübersicht" das reine Zahlungseingänge-Diagramm):
 * Einnahmen (Zahlungseingänge ohne USt), Ausgaben (netto) und Gewinn als Linie.
 */
export function FinanceChart({
  months,
  range,
  onRangeChange,
}: {
  months: FinanceMonth[];
  range: "6" | "12";
  onRangeChange: (v: "6" | "12") => void;
}) {
  const data = months.slice(range === "6" ? -6 : -12).map((m) => ({
    name: m.label,
    Einnahmen: m.incomeNetCents / 100,
    Ausgaben: m.expenseNetCents / 100,
    Gewinn: m.profitNetCents / 100,
  }));
  const empty = data.every((x) => x.Einnahmen === 0 && x.Ausgaben === 0);

  return (
    <div className="panel">
      <div className="flex items-center justify-between gap-4 mb-5">
        <div>
          <h2 className="panel-title">Einnahmen, Ausgaben &amp; Gewinn (netto)</h2>
          <p className="text-2xs text-text-tertiary mt-0.5">
            Einnahmen = Zahlungseingänge ohne USt · Gewinn = Einnahmen − Ausgaben, je Monat
          </p>
        </div>
        <div className="w-44 flex-shrink-0">
          <Select
            value={range}
            onChange={(e) => onRangeChange(e.target.value as "6" | "12")}
            options={[
              { value: "6", label: "Letzte 6 Monate" },
              { value: "12", label: "Letzte 12 Monate" },
            ]}
          />
        </div>
      </div>
      <div className="h-72">
        {empty ? (
          <div className="h-full flex items-center justify-center text-sm text-text-tertiary">
            Noch keine Einnahmen oder Ausgaben im Zeitraum.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data} margin={{ top: 4, right: 8, bottom: 0, left: 8 }}>
              <CartesianGrid stroke="rgb(var(--color-border-subtle))" vertical={false} />
              <XAxis dataKey="name" tick={{ fill: "rgb(var(--color-text-tertiary))", fontSize: 11 }} tickLine={false} axisLine={false} dy={6} />
              <YAxis
                tick={{ fill: "rgb(var(--color-text-tertiary))", fontSize: 11 }}
                tickLine={false}
                axisLine={false}
                width={78}
                tickFormatter={axisEuro}
              />
              <Tooltip
                cursor={{ fill: "rgb(var(--color-bg-hover))" }}
                contentStyle={{
                  background: "rgb(var(--color-bg-card))",
                  border: "1px solid rgb(var(--color-border-default))",
                  borderRadius: 10,
                  fontSize: 12,
                }}
                formatter={(v: number, name: string) => [formatMoney(Math.round(v * 100)), name]}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="Einnahmen" fill="rgb(var(--color-accent))" radius={[4, 4, 0, 0]} maxBarSize={22} isAnimationActive={false} />
              <Bar dataKey="Ausgaben" fill="rgb(var(--color-danger))" radius={[4, 4, 0, 0]} maxBarSize={22} isAnimationActive={false} />
              <Line type="monotone" dataKey="Gewinn" stroke="rgb(var(--color-success))" strokeWidth={2.5} dot={{ r: 3 }} isAnimationActive={false} />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
